import { FC, ReactNode, useContext } from 'react';
import { Box } from '@mui/material';
import moment from 'moment';
import { ApexLineChart } from '../../../../../PDFViewer/components/ApexChart';
import { AppContext } from '../../../../../../context';
import bundledPrLogo from '../../../../../../assets/images/pr-investment-logo.png';
import styles from './styles';
import { getPrTemplateVariant, PR_LOGO_PUBLIC_PATH } from '../templateConstants';
import {
  getPrKonditionenDefaults,
  getPrSectionLabels,
  getPrStammdatenDefaults,
  PrLocale,
} from '../prPreviewCopy';

interface FactsheetPreviewProps {
  data?: any;
  template?: any;
  lineChartLoading?: boolean;
  title?: string;
  subtitle?: string;
  isPreviewStarted?: boolean;
  onResetPreviewStarted?: (value: boolean) => void;
  onChartImageChange?: (dataUrl?: string) => void;
  onPieChartImagesChange?: (...args: any[]) => void;
  onRegisterChartCapture?: (capture: () => Promise<void>) => void;
  onDataChange?: (data: any) => void;
}

function formatDate(value?: string) {
  if (!value) return '';
  const parsed = moment(value, ['YYYY-MM-DD', 'DD.MM.YYYY', moment.ISO_8601], true);
  return parsed.isValid() ? parsed.format('DD.MM.YYYY') : String(value);
}

function displayValue(value: unknown, fallback = '—') {
  if (value == null) return fallback;
  const text = String(value).trim();
  if (!text || /^[—–−-]+$/.test(text)) return fallback;
  // Cleversoft Performance Fee: "5%" (no space) + break before Hurdle
  let next = text
    .replace(
      /15\s*%\s*p\.a\.\s*\(\s*High Watermark and\s*5\s*%\s*(?:\r?\n|\s+)*Hurdle\s*\)/gi,
      '15 % p.a. (High Watermark and 5%\nHurdle)'
    )
    .replace(
      /15\s*%\s*p\.a\.\s*\(\s*High Watermark und\s*5\s*%\s*(?:\r?\n|\s+)*Hurdle\s*\)/gi,
      '15 % p.a. (High Watermark und 5%\nHurdle)'
    );
  // Keep remaining "N %" pairs from wrapping to a lone "%"
  next = next.replace(/(\d(?:[.,]\d+)?)\s+%/g, '$1\u00A0%');
  return next;
}

const KvRow: FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={styles.kvRow}>
    <Box sx={styles.kvLabel}>{label}</Box>
    <Box sx={styles.kvValue}>{value}</Box>
  </Box>
);

const AllocRow: FC<{ label: string; percent?: number | string; comfort?: boolean }> = ({
  label,
  percent,
  comfort,
}) => (
  <Box sx={comfort ? styles.allocRowComfort : styles.allocRow}>
    <span>{label}</span>
    <span>
      {percent == null || percent === ''
        ? '—'
        : `${typeof percent === 'number' ? percent.toFixed(1) : percent} %`}
    </span>
  </Box>
);

const GreySection: FC<{ id: string; title: string; children: ReactNode; comfort?: boolean }> = ({
  id,
  title,
  children,
  comfort,
}) => (
  <Box id={id} sx={styles.greySection}>
    <Box sx={styles.greySectionTitle}>{title}</Box>
    <Box sx={comfort ? styles.greySectionBodyComfort : styles.greySectionBody}>{children}</Box>
  </Box>
);

export const FactsheetPreview: FC<FactsheetPreviewProps> = ({
  data,
  template,
  lineChartLoading,
}) => {
  const { document } = useContext(AppContext);
  const effective = data || {};
  const variant = getPrTemplateVariant(document?.template?._id || template?._id);
  const locale: PrLocale = variant?.locale ?? (effective.prLocale === 'en' ? 'en' : 'de');
  const shareClass = variant?.shareClass ?? (effective.prShareClass === 'P' ? 'P' : 'R');
  const labels = getPrSectionLabels(locale);
  const defaultKonditionen = getPrKonditionenDefaults(shareClass, locale);
  const defaultStammdaten = getPrStammdatenDefaults(locale);
  const logoUrl = bundledPrLogo || PR_LOGO_PUBLIC_PATH;
  const footerLabel =
    variant?.footerLabel ??
    (locale === 'de' ? `Factsheet - Anteilklasse ${shareClass}` : `Factsheet - Share Class ${shareClass}`);
  const reportDate = formatDate(effective.data_as_of || effective.published_at) || '—';
  const allocAsOf = formatDate(effective.allocations?.printedLabel) || reportDate;
  const stammdaten = { ...defaultStammdaten, ...(effective.stammdaten || {}) };
  const preise = effective.preise || {};
  const konditionen = { ...defaultKonditionen, ...(effective.konditionen || {}) };
  const isin = stammdaten.isin || variant?.isin || document?.template?.isins?.[0] || '';
  const wkn = stammdaten.wkn || variant?.wkn || '';
  const lineChartData = effective.lineChartData || [];
  const assetStructure: Array<{ label: string; percent: number }> = effective.allocations?.assetStructure || [];
  const countries: Array<{ label: string; percent: number }> = effective.allocations?.countries || [];
  const top5: Array<{ label: string; percent: number }> = effective.allocations?.top5 || [];
  const performanceRows: Array<{ label: string; value: string }> = effective.performanceRows || [
    { label: labels.perfOneMonth, value: '—' },
    { label: labels.perfYtd, value: '—' },
    { label: labels.perf5y, value: '—' },
    { label: labels.perf10y, value: '—' },
    { label: labels.perfSinceInception, value: '—' },
    { label: labels.totalReturn, value: '—' },
    { label: labels.volatilitySinceInception, value: '—' },
  ];

  return (
    <Box sx={styles.container}>
      <style>
        {`
          @font-face {
            font-family: 'Costa Pro';
            src: url('/fonts/pr/CostaPro-Regular.otf') format('opentype');
            font-weight: 400;
            font-style: normal;
            font-display: swap;
          }
          @font-face {
            font-family: 'Costa Pro';
            src: url('/fonts/pr/CostaPro-Bold.otf') format('opentype');
            font-weight: 700;
            font-style: normal;
            font-display: swap;
          }
        `}
      </style>
      <Box id="pr-header-section" sx={styles.header}>
        <Box sx={styles.headerLeft}>
          {/* Box — not Typography: theme body1 media query overrides sx fontSize */}
          <Box sx={styles.title}>{effective.title || 'P&R Real Value'}</Box>
          <Box sx={styles.subtitle}>{footerLabel}</Box>
        </Box>
        <Box sx={styles.headerRight}>
          <img
            src={logoUrl}
            alt=""
            style={styles.logo}
            onError={(event) => {
              const img = event.currentTarget;
              if (img.src.includes(PR_LOGO_PUBLIC_PATH)) {
                img.style.display = 'none';
                return;
              }
              img.src = PR_LOGO_PUBLIC_PATH;
            }}
          />
        </Box>
      </Box>

      <Box sx={styles.body}>
        <Box id="pr-investment-style-section">
          <Box sx={styles.sectionBar}>{labels.investmentStyle}</Box>
          <Box sx={styles.sectionBody}>
            <Box sx={styles.investmentStyle}>
              {displayValue(effective.investmentStyle, locale === 'en' ? 'Investment style text' : 'Investmentstil')}
            </Box>
          </Box>
        </Box>

        <Box sx={styles.columns}>
          <Box sx={styles.column}>
            <GreySection id="pr-prices-section" title={`${labels.pricesHeader} ${reportDate}`}>
              <KvRow label={labels.nav} value={displayValue(preise.nav || preise.mepreis)} />
              <KvRow label={labels.fundVolume} value={displayValue(preise.fondsvolumen || preise.fundVolume)} />
              <KvRow label={labels.minInvestment} value={displayValue(preise.minInvestment || variant?.minInvestment)} />
              <KvRow label={labels.bloomberg} value={displayValue(preise.bloomberg || variant?.bloomberg)} />
            </GreySection>

            <GreySection id="pr-fees-section" title={labels.conditions}>
              <KvRow label={labels.issueSurcharge} value={displayValue(konditionen.issueSurcharge)} />
              <KvRow label={labels.redemptionFee} value={displayValue(konditionen.redemptionFee)} />
              <KvRow label={labels.advisoryFee} value={displayValue(konditionen.advisoryFee)} />
              <KvRow
                label={labels.ongoingCosts}
                value={displayValue(konditionen.ongoingCostsNote || konditionen.laufendeKosten)}
              />
              <KvRow label={labels.performanceFee} value={displayValue(konditionen.performanceFee)} />
            </GreySection>

            <GreySection id="pr-master-data-section" title={labels.masterData}>
              <KvRow label={labels.isinWkn} value={`${isin || '—'} / ${wkn || '—'}`} />
              <KvRow label={labels.fundType} value={displayValue(stammdaten.fundType)} />
              <KvRow label={labels.investmentUniverse} value={displayValue(stammdaten.investmentUniverse)} />
              <KvRow label={labels.inceptionDate} value={displayValue(stammdaten.auflagedatum || variant?.inception)} />
              <KvRow label={labels.managementCompany} value={displayValue(stammdaten.managementCompany)} />
              <KvRow label={labels.depositary} value={displayValue(stammdaten.depositary)} />
              <KvRow label={labels.investmentAdviser} value={displayValue(stammdaten.investmentAdviser)} />
            </GreySection>

            <GreySection id="pr-portfolio-section" title={`${labels.assetStructure} ${allocAsOf}`}>
              {assetStructure.length ? (
                assetStructure.map((row) => <AllocRow key={row.label} label={row.label} percent={row.percent} />)
              ) : (
                <Box sx={styles.kvLabel}>—</Box>
              )}
            </GreySection>

            <GreySection id="pr-countries-section" title={`${labels.countryBreakdown} ${allocAsOf}`}>
              {countries.length ? (
                countries.map((row) => <AllocRow key={row.label} label={row.label} percent={row.percent} />)
              ) : (
                <Box sx={styles.kvLabel}>—</Box>
              )}
            </GreySection>
          </Box>

          <Box sx={styles.column}>
            <GreySection
              id="pr-performance-section"
              title={`${labels.performance} ${reportDate}`}
              comfort
            >
              {shareClass === 'R' && effective.shareClassHistoryNote ? (
                <Box sx={styles.historyNote}>{effective.shareClassHistoryNote}</Box>
              ) : null}
              <Box sx={styles.perfRow}>
                <Box />
                <Box sx={styles.perfHead}>{labels.fundColumn}</Box>
              </Box>
              {performanceRows.map((row) => (
                <Box key={row.label} sx={styles.perfRow}>
                  <Box sx={styles.perfLabel}>{row.label}</Box>
                  <Box sx={styles.perfValue}>{displayValue(row.value)}</Box>
                </Box>
              ))}
              <Box id="pr-chart-section" sx={styles.chartBox}>
                {lineChartData.length ? (
                  <ApexLineChart
                    id="pr-history-chart"
                    data={lineChartData}
                    // Preview API returns [[ts, pct], …] — same as Axxion GET_QUERY; without
                    // isSolActive Apex remaps as {timestamp,value} and the series goes blank.
                    isSolActive
                    color={document?.template?.color || '#1B3A5C'}
                    loading={lineChartLoading}
                    showToolbar={false}
                    height={220}
                    seriesName="P&R Real Value - P"
                    yAxisPrefix=""
                    yAxisFormatter={(value) =>
                      `${Number.isFinite(value) ? Math.round(value).toLocaleString('de-DE') : value} %`
                    }
                    axisLabelFontSize={13}
                    axisLabelColor="#444444"
                  />
                ) : (
                  <Box sx={styles.chartPlaceholder} />
                )}
                <Box sx={styles.chartCaption}>{labels.chartSource}</Box>
              </Box>
            </GreySection>

            <GreySection id="pr-top5-section" title={`${labels.top5} ${allocAsOf}`} comfort>
              {top5.length ? (
                top5.map((row) => (
                  <AllocRow key={row.label} label={row.label} percent={row.percent} comfort />
                ))
              ) : (
                <Box sx={styles.kvLabel}>—</Box>
              )}
            </GreySection>
          </Box>
        </Box>

        <Box id="pr-disclaimer-section" sx={styles.footnotes}>
          <Box sx={styles.disclaimer}>{displayValue(effective.disclaimer)}</Box>
        </Box>
      </Box>
    </Box>
  );
};
